import { yup } from '@reapitform/utils'
import { AdminFormVersionFlags } from '../../api/types'

/** Values for the shared form-details form (form fields + version fields + flags), used by create + edit. */
export interface FormDetailsValues extends AdminFormVersionFlags {
  name: string
  fullName: string
  formType: string
  region: string
  country: string
  version: string
  validFromUTC: string
}

/**
 * The version feature-flags rendered as checkboxes, in display order, with human-readable labels and a
 * group. `group: 'agreement-only'` doesn't mean the flag is inert on a Notice — RecipientComponentFactory/
 * RepresenteeComponentFactory still read it unconditionally whenever a PersonRecipient/PersonRepresentee
 * section exists, so it still has real effect. It means the *concept* only makes practical sense for an
 * Agreement (tenant-signing consent, or consent for how future notices *about* an agreement are served —
 * not to be confused with the Notices document family itself). Grouped and shown regardless of family
 * rather than hidden: a stored `true` for a hidden flag would still silently inject its fields.
 */
export const VERSION_FLAGS: {
  name: keyof AdminFormVersionFlags
  label: string
  group: 'applies' | 'agreement-only'
}[] = [
  // The pre-existing "service of notice" consent concept (NoticeType/NoticeAttribute) - a tenant's
  // nominated method for receiving future notices served *under* an agreement. Deliberately never named
  // "Notice"/"NoticeType" in these labels, despite the historical property name, so it isn't confused with
  // the Notices document family this checkbox sits next to.
  {
    name: 'noticesOnlyForPrimary',
    label: 'Service-of-notice consent: primary recipient only',
    group: 'agreement-only',
  },
  { name: 'allowEmailNotices', label: 'Collect consent: service of notice by email', group: 'agreement-only' },
  {
    name: 'allowFacsimileNotices',
    label: 'Collect consent: service of notice by facsimile',
    group: 'agreement-only',
  },
  {
    name: 'allowTextMessageNotices',
    label: 'Collect consent: service of notice by text message',
    group: 'agreement-only',
  },
  { name: 'allowRepresenteeAddress', label: 'Allow representee address', group: 'applies' },
  { name: 'allowRecipientAddress', label: 'Allow recipient address', group: 'applies' },
  { name: 'recipientAddressDefaultToProperty', label: 'Recipient address defaults to property', group: 'applies' },
  { name: 'allowRecipientEmergencyContacts', label: 'Allow recipient emergency contacts', group: 'applies' },
  { name: 'allowRepresenteeName2', label: 'Allow second representee name', group: 'applies' },
  { name: 'ownerContactDetailsDefaultToAgency', label: 'Owner contact details default to agency', group: 'applies' },
  {
    name: 'showOwnerContactDetailsForRecipient',
    label: 'Show owner contact details to recipient',
    group: 'applies',
  },
  // A Notice is never signed by a tenant/representee at all, so this flag's only effect - enabling that
  // signing UI - can never surface on a Notice regardless of its stored value.
  { name: 'allowRepresenteeSigning', label: 'Allow representee signing', group: 'agreement-only' },
]

/** Flag defaults, matching the AdminFormVersionModel initializers on the backend. */
export const versionFlagDefaults: AdminFormVersionFlags = {
  noticesOnlyForPrimary: true,
  allowEmailNotices: true,
  allowFacsimileNotices: true,
  allowTextMessageNotices: true,
  allowRepresenteeAddress: false,
  allowRecipientAddress: false,
  recipientAddressDefaultToProperty: true,
  allowRecipientEmergencyContacts: false,
  allowRepresenteeName2: false,
  ownerContactDetailsDefaultToAgency: true,
  showOwnerContactDetailsForRecipient: false,
  allowRepresenteeSigning: false,
}

/** Shared version-level fields, used standalone when adding a version to a form that already exists (no
 * form-level fields to collect — those belong to the form, not the new version) as well as inside the
 * combined create/edit schema below. */
const versionFieldsShape = {
  version: yup.string().required('Enter a version label'),
  validFromUTC: yup.string().required('Select the valid-from date'),
  noticesOnlyForPrimary: yup.boolean().default(true),
  allowEmailNotices: yup.boolean().default(true),
  allowFacsimileNotices: yup.boolean().default(true),
  allowTextMessageNotices: yup.boolean().default(true),
  allowRepresenteeAddress: yup.boolean().default(false),
  allowRecipientAddress: yup.boolean().default(false),
  recipientAddressDefaultToProperty: yup.boolean().default(true),
  allowRecipientEmergencyContacts: yup.boolean().default(false),
  allowRepresenteeName2: yup.boolean().default(false),
  ownerContactDetailsDefaultToAgency: yup.boolean().default(true),
  showOwnerContactDetailsForRecipient: yup.boolean().default(false),
  allowRepresenteeSigning: yup.boolean().default(false),
}

export const formDetailsSchema = yup.object({
  name: yup.string().required('Enter a form name'),
  fullName: yup.string().optional().default(''),
  formType: yup.string().required('Select a form type'),
  region: yup.string().optional().default(''),
  country: yup.string().required('Select a country'),
  ...versionFieldsShape,
})

/** Version-only variant — the form-level fields (name/formType/region/country) aren't rendered or
 * required, since adding a version to an existing form never touches the Form entity itself. */
export const versionDetailsSchema = yup.object({
  name: yup.string().optional().default(''),
  fullName: yup.string().optional().default(''),
  formType: yup.string().optional().default(''),
  region: yup.string().optional().default(''),
  country: yup.string().optional().default(''),
  ...versionFieldsShape,
})
